/**
 * Driver shift report printer — «Αναφορά Βάρδιας Οδηγού».
 *
 * Mirrors the X/Z report pattern but scoped to a single driver. The
 * report renders on 76mm thermal paper (or browser preview) and
 * contains:
 *
 *   • Driver name + 8888XXXX barcode (Code128)
 *   • Shift open/close timestamps
 *   • 3 aggregate metrics (count, total sales, average delivery time)
 *   • Itemised list: order_code · customer · amount · delivery time
 *
 * Designed to be called from `DeliveryDriverHub::DriverOrdersDrillDown`
 * with the same orders[] array already loaded in the dialog, so the
 * print happens with zero extra round-trip.
 */
import { printReportSmart } from "@/lib/kioskMode";


const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("el-GR"); } catch { return "—"; }
};

const fmtMinutes = (m) => {
  if (m === null || m === undefined) return "—";
  if (m < 60) return `${Math.round(m)}′`;
  const h = Math.floor(m / 60);
  const r = Math.round(m % 60);
  return `${h}ω ${r}′`;
};

const fmtEUR = (n) =>
  `${(Number(n) || 0).toFixed(2).replace(".", ",")} €`;

/**
 * Compute the same stats the drill-down dialog displays so the printed
 * report stays consistent with the on-screen numbers.
 */
function computeStats(orders) {
  const count = orders.length;
  const delivered = orders.filter((o) => o.status === "DELIVERED").length;
  const totalSales = orders.reduce((s, o) => s + Number(o.total || 0), 0);
  const durations = orders
    .map((o) => {
      if (Number.isFinite(o.delivery_duration_seconds)) {
        return Math.max(0, o.delivery_duration_seconds / 60);
      }
      if (!o.delivered_at || !o.created_at) return null;
      const t0 = new Date(o.created_at).getTime();
      const t1 = new Date(o.delivered_at).getTime();
      return Number.isFinite(t1 - t0) ? (t1 - t0) / 60000 : null;
    })
    .filter((v) => v !== null && v >= 0);
  const avgDeliveryMin = durations.length
    ? durations.reduce((s, v) => s + v, 0) / durations.length
    : null;
  return { count, delivered, totalSales, avgDeliveryMin, durations };
}

/**
 * Build a plain-text 46-column version of the driver shift report. The
 * Companion agent v3.x wraps this in ESC/POS and prints silently — no
 * browser print dialog that would «freeze» the POS.
 */
function buildPlainText({ driver, shift, orders, stats, driverBarcode }) {
  const W = 46;
  const center = (s) => {
    const t = String(s || "");
    if (t.length >= W) return t.slice(0, W);
    const pad = Math.floor((W - t.length) / 2);
    return " ".repeat(pad) + t;
  };
  const cap = (s, n) => {
    const t = String(s || "");
    return t.length <= n ? t.padEnd(n) : t.slice(0, n - 1) + "…";
  };
  const sep = (ch = "-") => ch.repeat(W);
  const lr = (l, r) => {
    const L = String(l || "");
    const R = String(r || "");
    const gap = Math.max(1, W - L.length - R.length);
    return L + " ".repeat(gap) + R;
  };
  const lines = [];
  lines.push(center("ΑΝΑΦΟΡΑ ΒΑΡΔΙΑΣ ΟΔΗΓΟΥ"));
  lines.push(center(driver.name || "—"));
  lines.push(center(driverBarcode));
  lines.push(sep("="));
  lines.push(lr("Άνοιγμα:", fmtTime(shift?.opened_at)));
  lines.push(lr("Κλείσιμο:", shift?.closed_at ? fmtTime(shift.closed_at) : "—"));
  lines.push(lr("Εκτύπωση:", new Date().toLocaleString("el-GR")));
  lines.push(sep("="));
  lines.push(lr("Παραγγελίες:", `${stats.count} (${stats.delivered} παραδ.)`));
  lines.push(lr("Πωλήσεις:", fmtEUR(stats.totalSales)));
  lines.push(lr("Μέσος όρος:", stats.count ? fmtEUR(stats.totalSales / stats.count) : "—"));
  lines.push(lr("Μ.Ο. παράδοσης:", fmtMinutes(stats.avgDeliveryMin)));
  lines.push(sep("="));
  // Rows: #code | customer | amount | duration
  lines.push(cap("#", 5) + cap("Πελάτης", 22) + cap("Ποσό", 10) + cap("Δρκ.", 9));
  lines.push(sep("-"));
  if (!orders.length) {
    lines.push(center("— καμία παραγγελία —"));
  } else {
    for (const o of orders) {
      const code = String(o.code || 0).padStart(4, "0");
      const name = o.customer_name || "—";
      const amount = fmtEUR(o.total || 0);
      const dur = Number.isFinite(o.delivery_duration_seconds)
        ? fmtMinutes(o.delivery_duration_seconds / 60)
        : (o.created_at && o.delivered_at)
          ? fmtMinutes((new Date(o.delivered_at).getTime() - new Date(o.created_at).getTime()) / 60000)
          : "—";
      lines.push(cap(code, 5) + cap(name, 22) + cap(amount, 10) + cap(dur, 9));
    }
  }
  lines.push(sep("="));
  lines.push(lr("Σύνολο:", fmtEUR(stats.totalSales)));
  lines.push(center("— Τέλος Αναφοράς —"));
  lines.push("");
  lines.push("");
  return lines.join("\n");
}

/**
 * Open a new window with the printable report HTML. Renders the driver
 * barcode via JSBarcode (lazy-imported so the chunk only loads when the
 * operator actually requests a print).
 */
export async function printDriverShiftReport({ driver, shift, orders, reportsPrinter = "" }) {
  const stats = computeStats(orders);
  const driverBarcode = `8888${String(driver.code || 0).padStart(4, "0")}`;
  const plainText = buildPlainText({ driver, shift, orders, stats, driverBarcode });
  // Pre-render the barcode SVG in the parent document, then inline
  // it into the HTML template — same approach as proforma.js. Iframe
  // print avoids the popup blocker on production HTTPS.
  let barcodeSvg = "";
  try {
    const { default: JsBarcode } = await import("jsbarcode");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    JsBarcode(svg, driverBarcode, {
      format: "CODE128", width: 1.6, height: 40, displayValue: false, margin: 0,
    });
    barcodeSvg = svg.outerHTML;
  } catch {
    /* jsbarcode unavailable — print without barcode */
  }
  const rows = orders
    .map((o) => {
      const code = String(o.code || 0).padStart(4, "0");
      const created = o.created_at ? new Date(o.created_at).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }) : "—";
      const delivered = o.delivered_at ? new Date(o.delivered_at).toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit" }) : "—";
      const dur = Number.isFinite(o.delivery_duration_seconds)
        ? fmtMinutes(o.delivery_duration_seconds / 60)
        : (o.created_at && o.delivered_at)
          ? fmtMinutes((new Date(o.delivered_at).getTime() - new Date(o.created_at).getTime()) / 60000)
          : "—";
      return `<tr>
        <td class="c">#${code}</td>
        <td class="l">${esc(o.customer_name || "—")}</td>
        <td class="r">${fmtEUR(o.total || 0)}</td>
        <td class="c">${created}</td>
        <td class="c">${delivered}</td>
        <td class="r">${dur}</td>
      </tr>`;
    })
    .join("");

  const html = `<!doctype html><html lang="el"><head><meta charset="utf-8"/>
<title>Αναφορά Βάρδιας — ${esc(driver.name)}</title>
<style>
  @page { size: 76mm auto; margin: 0; }
  html, body { margin: 0; padding: 0; background:#fff; color:#000;
    font-family: "Helvetica","Arial",sans-serif; font-size: 11px; }
  .wrap { padding: 4mm 3mm; }
  .h1 { text-align:center; font-size: 14px; font-weight: 900;
        letter-spacing:.05em; }
  .h2 { text-align:center; font-size: 12px; font-weight: 700;
        margin-top:1mm; }
  .meta { font-size: 10px; margin: 2mm 0; }
  .meta div { display:flex; justify-content:space-between; padding:.5mm 0; }
  hr.solid { border:none; border-top:1.5px solid #000; margin:2mm 0; }
  hr.dotted { border:none; border-top:1px dashed #888; margin:1.5mm 0; }
  .bc { text-align:center; margin: 2mm 0; }
  .bc svg { width: 60mm; height: 12mm; }
  .bc .code { font-family: monospace; font-size:9px; margin-top:.5mm; }
  .stats { display:grid; grid-template-columns: 1fr 1fr; gap: 1.5mm;
           margin: 2mm 0; }
  .stat { border: 1.5px solid #000; padding: 1.5mm; text-align:center; }
  .stat .lbl { font-size: 8px; text-transform: uppercase;
               letter-spacing:.05em; font-weight: 700; }
  .stat .val { font-size: 14px; font-weight: 900; font-family: monospace;
               margin-top: .5mm; }
  table.orders { width:100%; border-collapse: collapse; font-size: 9px;
                 margin-top: 1mm; }
  table.orders th, table.orders td { padding: .8mm 1mm;
                                      border-bottom: 1px dotted #999; }
  table.orders th { font-size: 8px; text-transform: uppercase;
                    text-align:left; border-bottom: 1px solid #000; }
  .l { text-align:left; }
  .c { text-align:center; }
  .r { text-align:right; font-family: monospace; }
  .total { text-align: right; font-weight: 900; font-size: 13px;
           border-top: 2px solid #000; margin-top: 2mm; padding-top: 1mm; }
  .footer { text-align:center; font-size: 9px; margin-top: 3mm;
            color:#666; }
  @media print { .wrap { padding: 3mm 2mm; } }
</style></head><body>
  <div class="wrap">
    <div class="h1">ΑΝΑΦΟΡΑ ΒΑΡΔΙΑΣ ΟΔΗΓΟΥ</div>
    <div class="h2">${esc(driver.name || "—")}</div>
    <div class="bc">
      <svg id="bc"></svg>
      <div class="code">${driverBarcode}</div>
    </div>
    <hr class="solid" />
    <div class="meta">
      <div><span>Άνοιγμα βάρδιας:</span><span>${fmtTime(shift?.opened_at)}</span></div>
      <div><span>Κλείσιμο βάρδιας:</span><span>${shift?.closed_at ? fmtTime(shift.closed_at) : "—"}</span></div>
      <div><span>Εκτύπωση:</span><span>${new Date().toLocaleString("el-GR")}</span></div>
    </div>
    <hr class="solid" />
    <div class="stats">
      <div class="stat">
        <div class="lbl">Παραγγελίες</div>
        <div class="val">${stats.count}</div>
        <div class="lbl">${stats.delivered} παραδόθηκαν</div>
      </div>
      <div class="stat">
        <div class="lbl">Πωλήσεις</div>
        <div class="val">${fmtEUR(stats.totalSales)}</div>
        <div class="lbl">μ.ο. ${stats.count ? fmtEUR(stats.totalSales / stats.count) : "—"}</div>
      </div>
      <div class="stat" style="grid-column: span 2">
        <div class="lbl">Μέσος όρος παράδοσης</div>
        <div class="val">${fmtMinutes(stats.avgDeliveryMin)}</div>
        <div class="lbl">από καταχώρηση έως παράδοση</div>
      </div>
    </div>
    <hr class="solid" />
    <table class="orders">
      <thead>
        <tr>
          <th class="c">#</th>
          <th class="l">Πελάτης</th>
          <th class="r">Ποσό</th>
          <th class="c">Κατ.</th>
          <th class="c">Παρ.</th>
          <th class="r">Διάρκ.</th>
        </tr>
      </thead>
      <tbody>${rows || `<tr><td colspan="6" class="c">— καμία παραγγελία —</td></tr>`}</tbody>
    </table>
    <div class="total">Σύνολο: ${fmtEUR(stats.totalSales)}</div>
    <div class="footer">— Τέλος Αναφοράς —</div>
  </div>
</body></html>`;
  // Inline the pre-rendered barcode SVG.
  const finalHtml = html.replace('<svg id="bc"></svg>', barcodeSvg);
  return printReportSmart({ text: plainText, html: finalHtml, route: reportsPrinter, afterMs: 300 });
}
